import { IEventCallbackVOSubscriber } from 'library/ts/events/interfaces/IEventCallbackVOSubscriber';
import { IEventDispatcher } from 'library/ts/events/interfaces/IEventDispatcher';
import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IImporterResolve } from 'library/ts/interfaces/importer/IImporterResolve';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IAPIMetadataItem } from 'library/ts/interfaces/metadata/api/IAPIMetadataItem';
import { ISettingsVO } from 'library/ts/interfaces/settings/ISettingsVO';
import { ICapabilities } from 'library/ts/interfaces/utilities/capabilities/ICapabilities';
import { Country } from 'library/ts/model/enums/geo/Country';
import { Domain } from 'library/ts/model/enums/web/Domain';
import { PlayerAdapterStreamType } from 'library/ts/player/adapter/enums/PlayerAdapterStreamType';
import { ILoadPluginScriptsArgument } from 'library/ts/plugins/shared/interfaces/arguments/ILoadPluginScriptsArgument';
import { TPluginsDictionary } from 'library/ts/plugins/shared/types/TPluginsDictionary';
import { PluginUtilities } from 'library/ts/plugins/shared/utilities/PluginUtilities';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { TagEvent, tagEvents } from 'tag/ts/tag/events/TagEvent';
import { IAbstractTagClass } from 'tag/ts/tag/interfaces/abstract/IAbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { ITagConfig } from 'tag/ts/tag/interfaces/config/ITagConfig';
import { ITagEventContextVO } from 'tag/ts/tag/interfaces/events/ITagEventContextVO';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { ITagConstants } from 'tag/ts/tag/interfaces/model/ITagConstants';
import { ITagMVC } from 'tag/ts/tag/interfaces/mvc/ITagMVC';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { TagConstants } from 'tag/ts/tag/model/TagConstants';
import { getBrand } from 'tag/ts/tag/utilities/BrandResolver';
import { TTagAppEventCallback } from 'tag/ts/tag/types/TTagAppEventCallback';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Abstract Class for Tag application classes
 */
export abstract class AbstractTagClass implements IAbstractTagClass {
	readonly #name: string;
	readonly #tag: ITag;
	#logger: ILogger;
	#plugins: TPluginsDictionary | undefined;

	protected constructor( argument: ITagAbstractClassArgument = { name: 'AbstractTagClass', tag: undefined } ) {
		this.#name = argument?.name;
		this.#tag = argument?.tag;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', 'Tag:', this.#tag.uuid, this.#name ] } );

		/**
		 * @description Informs concrete class about plugins load / init phase completion
		 */
		const pluginsPhaseDone = (): void => {
			if ( this.utils.Type.isFunction( argument?.onPluginsPhaseDone ) ) {
				argument?.onPluginsPhaseDone( this.plugins );
			}
		};

		// initialize plugins
		this.#initializePlugins().then(
			( data ) => {
				if ( this.utils.Type.isFunction( argument?.resolve ) ) {
					argument?.resolve( { msg: `${this.name} class is ready`, success: true, result: data } );
				}
				pluginsPhaseDone();
			}
		).catch(
			( data ) => {
				if ( this.utils.Type.isFunction( argument?.reject ) ) {
					argument?.reject( { msg: `${this.name} class has failed`, success: false, result: data } );
				}
				pluginsPhaseDone();
			}
		);
	}

	/**
	 * @description Initialize plugins for this class
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	#initializePlugins = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				this.utils.Delay.delay().then(
					() => {
						PluginUtilities.loadPluginScripts(
							{
								uuid: this.uuid,
								config: this?.tag?.model?.config?.tag,
								ownerClassName: this.name,
								brand: getBrand()
							}
						).then(
							( data ) => {
								// reduce console noise
								if ( !data?.msg.toString().includes( 'No plugins marked for pre-load were found' ) ) {
									this?.logger?.log( 'initializePlugins:', 'loadPluginScripts:', data.msg, data );
								}
								this.setupPlugins(
									{
										ownerClass: this,
										framework: {
											model: this.tag.model,
											view: this.tag.view,
											controller: this.tag.controller
										} as ITagMVC,
										config: this.tag.model.config.tag
									}
								).then(
									( data ) => {
										// reduce console noise
										if ( !data?.msg.toString().includes( 'No plugins configs were found' ) ) {
											this?.logger?.log( 'initializePlugins:', data.msg, data );
										}
										resolve( { msg: `${this.name} plugins were initialized.` } );
									}
								).catch(
									( data ) => {
										reject( { msg: `${this.name} plugins initialization has failed.`, result: data } );
									}
								);
							}
						).catch(
							( data ) => {
								reject( { msg: `${this.name} plugins load has failed.`, result: data } );
							}
						);
					}
				);
			}
		);
	};

	/**
	 * @description Name of this class
	 * @return {string}
	 */
	get name(): string {
		return this.#name;
	}

	get tag() {
		return this.#tag;
	}

	/**
	 * @description Returns tag MVC instance (model/view/controller combined into single object)
	 * @return {ITagMVC}
	 */
	get tagMVC(): ITagMVC {
		return {
			model: this?.tag?.model,
			view: this?.tag?.view,
			controller: this?.tag?.controller
		};
	}

	get logger() {
		return this.#logger;
	}

	/**
	 * @description UUID of this Tag
	 * @return {string}
	 */
	get uuid(): string {
		return this.#tag.uuid;
	}

	/**
	 * @description Params Value Object of this Tag
	 * @return {IParamsVO}
	 */
	get params(): IParamsVO {
		return this.#tag?.params;
	}

	set params( value ) {
		this.#tag.params = value;
	}

	/**
	 * @description Reference to callbacks object passed to Smart Tag from DOM
	 * @returns {ICallbacksVO}
	 */
	get callbacks(): ICallbacksVO {
		return this.#tag?.callbacks;
	}

	set callbacks( value: ICallbacksVO ) {
		this.#tag.callbacks = value;
	}

	/**
	 * @description Returns api metadata for current video
	 * @returns {IAPIMetadataItem | undefined}
	 */
	get apiMetadata(): IAPIMetadataItem | undefined {
		return this?.tag?.model?.apiMetadata;
	}

	/**
	 * @description Returns country code
	 * @returns {Country}
	 */
	get country() {
		return (
			this?.params?.CONTENT?.country ||
			this?.runtimeVO?.top?.country ||
			Country.US
		);
	}

	/**
	 * @description Reference to instance of Utilities class instance
	 * @returns {Utilities}
	 */
	get utils(): Utilities {
		return window?.SmartTag?.utilities;
	}

	/**
	 * @description Reference to instance of Capabilities class instance
	 * @return {ICapabilities}
	 */
	get capabilities(): ICapabilities {
		return window?.SmartTag?.capabilities;
	}

	/**
	 * @description Reference to Runtime Value Object
	 * @return {ISmartTagRuntimeVO}
	 */
	get runtimeVO(): ISmartTagRuntimeVO {
		return window?.SmartTag?.runtimeVO;
	}

	/**
	 * @description This Tag Constants
	 * @return {ITagConstants}
	 */
	get TagConstants(): ITagConstants {
		return TagConstants.getInstance();
	}

	/**
	 * @description Returns tag configuration
	 */
	get tagConfig() {
		return this?.tag?.model?.config?.tag as ITagConfig;
	}

	/**
	 * @description Sets / Returns object containing references to plugins
	 * @return {TPluginsDictionary | undefined}
	 */
	get plugins(): TPluginsDictionary | undefined {
		return this.#plugins;
	}

	set plugins( value: TPluginsDictionary | undefined ) {
		this.#plugins = value;
	}

	/**
	 * @description Returns reference to PluginUtilities.setupPluginsForOwnerClass;
	 * @returns {(argument?: ISetupPluginsForOwnerClassArgument) => Promise<IPromiseResolveGeneric & {plugins: TPluginsDictionary}>}
	 */
	get setupPlugins() {
		return PluginUtilities.setupPluginsForOwnerClass;
	}

	/**
	 * @description Compound method for loading + initialization of plugins at runtime - after application was already initialized
	 * @param {ILoadPluginScriptsArgument} argument
	 * @returns {Promise<IPromiseResolveGeneric & {plugins: TPluginsDictionary}>}
	 */
	initializePluginsAtRuntime = ( argument: ILoadPluginScriptsArgument ): Promise<IPromiseResolveGeneric & { plugins: TPluginsDictionary }> => {
		return new Promise(
			( resolve, reject ) => {
				PluginUtilities.loadPluginScripts(
					{ ...argument, brand: argument?.brand ?? getBrand() }
				).then(
					( data: IImporterResolve & { msg?: string } | IImporterReject & { msg?: string } ) => {
						this.setupPlugins(
							{
								ownerClass: this,
								framework: this.tagMVC,
								config: this.tagConfig
							}
						).then(
							( plugins: IPromiseResolveGeneric & { plugins: TPluginsDictionary } ) => {
								resolve( { msg: `${this.name}: plugins instantiated:`, plugins: plugins, success: true } );
							}
						);
					}
				);
			}
		);
	};

	/**
	 * @description Returns Tag out API => for calls to page
	 * @return {ITagAPIOut}
	 */
	get TAG_API() {
		return this.tag?.controller?.tagAPI?.OUT;
	}

	/**
	 * @description Dispatches tag application event
	 * @return {(eventVO: ITagEventVO | any, timeout?: number) => void}
	 */
	get dispatchTagEvent(): (
		eventVO: ITagEventVO,
		timeout?: number
	) => void {
		return this.#tag.dispatchTagEvent;
	}

	/**
	 * @description Returns instance of TagEvent Value Object
	 * @returns {(type?: TagEvent, tag?: ITag, data?: ITagEventDataVO, target?: any, targetName?: string) => ITagEventVO}
	 */
	get getTagEventVO(): (
		type?: TagEvent,
		tag?: ITag,
		data?: ITagEventDataVO,
		target?: any,
		targetName?: string
	) => ITagEventVO {
		return this.#tag.getTagEventVO;
	}

	/**
	 * @description Returns instance of TagEvent Data Value Object
	 * @returns {() => ITagEventDataVO}
	 */
	get getTagEventDataVO(): () => ITagEventDataVO {
		return this.#tag.getTagEventDataVO;
	}

	/**
	 * @description Adds event listeners for all events dispatched within this Tag instance.
	 * Redirects events to a Callback function passed from a Concrete class as a parameter
	 * @param {TTagAppEventCallback} callback
	 * @param {(TagEvent | ITagEventContextVO)[]} events
	 * @param {IEventCallbackVOSubscriber} subscriber
	 */
	tagEventsHandler = (
		callback: TTagAppEventCallback,
		events: ( TagEvent | ITagEventContextVO ) [],
		subscriber: IEventCallbackVOSubscriber = this
	): void => {
		this.#tag.tagEventsHandler( callback, events, subscriber );
	};

	/**
	 * @description Remove events from dispatcher object
	 * @param callback
	 * @param {IEventDispatcher} eventDispatcher
	 * @param {TagEvent[] | TagEvent} events
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	removeTagEventsHandler = (
		callback,
		eventDispatcher: IEventDispatcher = this?.tag?.eventDispatcher,
		events: TagEvent[] | TagEvent = tagEvents()
	): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				if (
					this.utils.Type.isArray( events ) === false
					&&
					this.utils.Type.isString( events ) === true
				) {
					events = [ events as TagEvent ]; // sanity check + array wrap if a single event string is passed
				}
				if (
					this.utils.Type.isDefined( callback )
					&&
					this.utils.Type.isFunction( callback )
				) {
					for ( let i = 0; i < events.length; i++ ) {
						eventDispatcher.removeEventListener( events[ i ], callback );
					}
				}
				resolve( { msg: 'Event listeners removed' } );
			}
		);
	};

	/**
	 * @description Local storage name
	 * @returns {string} storage key, or undefined if the Tag's partner can't be
	 * resolved (e.g. it has already been torn down and removed from the registry)
	 */
	get localStorageName(): string {
		const partner = this?.params?.CONTENT?.partner;
		// No resolvable partner — return undefined rather than a key containing the
		// literal string "undefined", so callers can detect it.
		if ( !partner ) {
			return undefined;
		}
		return `smart-tag-${partner}`;
	}

	/**
	 * @description Reads + returns settings object
	 * @return {ISettingsVO} settings object, or undefined if localStorageName can't be resolved
	 */
	get settings(): ISettingsVO {
		const key = this?.localStorageName;
		if ( !key ) {
			return undefined;
		}
		return this?.utils?.Store?.get?.( key );
	}

	/**
	 * @description Returns boolean indicating if host device is desktop
	 * @returns {boolean}
	 */
	protected isDesktop(): boolean {
		return !!window?.SmartTag?.capabilities?.isDesktop;
	}

	/**
	 * @description Returns boolean indicating if host domain is paramountplus
	 * @returns {boolean}
	 */
	protected isPPlus(): boolean {
		this.#logger?.log( 'isPPlus()==>', !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.PPLUS ) );
		return !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.PPLUS );
	}

	/**
	 * @description Returns boolean indicating if host domain is cbs
	 * @returns {boolean}
	 */
	protected isCBS(): boolean {
		this.#logger?.log( 'isCBS()==>', !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.CBSCOM ) );
		return !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.CBSCOM );
	}

	/**
	 * @description Returns boolean indicating if host domain is pluto tv
	 * @returns {boolean}
	 */
	protected isPTV(): boolean {
		this.#logger?.log( 'isPTV()==>', !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.PTV ) );
		return !!this?.runtimeVO?.top?.parsed?.host?.includes?.( Domain.PTV );
	}

	/**
	 * @description Returns a boolean flag for assetURL live stream or DAI live stream
	 * @returns {boolean}
	 */
	protected isLive(): boolean {
		if ( this.isPTV() ) {
			const isPtvLive = this.params?.STITCHER?.streamType?.toUpperCase() === PlayerAdapterStreamType.LIVE;
			this.#logger?.log( 'isLive()==>', isPtvLive );
			return isPtvLive;
		}
		const result = !!this?.tag?.model?.contentUtilities?.isLive?.( this.params, this.apiMetadata );
		this.#logger?.log( 'isLive()==>', result );
		return result;
	}

	/**
	 * @description Returns country code for current session
	 * @returns {string}
	 */
	protected getCountryCode(): string {
		return this?.tag?.country?.toString();
	}

	/**
	 * @description Returns boolean indicating if current asset is using DAI
	 * @return {boolean}
	 */
	protected isDAIAsset(): boolean {
		return this?.tag?.model?.contentUtilities?.isDAIAsset();
	}

	/**
	 * @description Returns boolean indicating if there is DAI Live Stream in MPX Metadata videoProperties array
	 * @return {boolean}
	 */
	protected isDaiLiveStream(): boolean {
		return this?.tag?.model?.contentUtilities?.isLiveDAIStream( this?.tag?.model?.apiMetadata );
	}

	/**
	 * Returns boolean indicating if current params contains STITCHER params
	 * @returns {boolean}
	 */
	protected hasStitcherParams(): boolean {
		return Object.keys( this.tag.params?.STITCHER ?? {} ).length > 0;
	}
}